/** @jsx jsx */
import { jsx, Container, Text, Box, Heading } from 'theme-ui'
import { FC, useState } from 'react'
import Layout from '~/components/layout'
import { useCartContext } from '~/context/cart-context'
import { CheckoutLineItemInput, CheckoutProductVariant } from '../@types/models'
import { space } from '~/gatsby-plugin-theme-ui'
import { CartItem } from '~/components/cart-item'
import { Button } from '~/components/button'
import CheckoutFlowLayout from '~/components/checkout-flow-layout'
import useCheckoutLink from '~/hooks/use-checkout-link'
import useQuantityNote from '~/hooks/use-quantity-note'
import { formatDate, formatMoney } from '~/utils/format'
import { lineItemsSummary, lineItemPrice } from '~/utils/price'
import { useCustomerContext } from '~/context/customer-context'
import { trackAddToCart, trackViewCart } from '~/utils/marketing/track'

const CheckoutButton = () => {
  const checkoutLink = useCheckoutLink()
  return (
    <Button
      as="link"
      to={checkoutLink}
      label="Proceed to Checkout"
      sxProps={{
        width: '100%',
        fontSize: ['12px', '16px'],
      }}
    />
  )
}

const CartSummaryPage = () => {
  const [loading] = useState(false)
  const { checkout, items, saveItem } = useCartContext()
  const { getNoteBySelectedQuantity } = useQuantityNote({})
  const { customerType } = useCustomerContext()
  const totalPrice = lineItemsSummary(customerType.trade, items)

  const updateQuantity = async (
    variant: CheckoutProductVariant,
    title: string,
    newValue: number
  ) => {
    const currentItem = items && items.find(item => item.variant.shopifyId === variant.shopifyId)
    const message = getNoteBySelectedQuantity(
      variant.quantityAvailable,
      ((currentItem && currentItem.quantity) as number) + newValue
    )

    const updatedItem: CheckoutLineItemInput = {
      variantId: variant.id,
      quantity: newValue,
      productType: currentItem?.productType,
      customAttributes: [
        {
          key: `Quantity`,
          value: message || '',
        },
      ],
    }

    await saveItem(updatedItem, title, variant)
  }

  const contents = items.map(item => {
    return {
      item_id: item.id,
      item_name: item.title,
      item_category: item.productType,
      item_variant: item.color,
      price: lineItemPrice(false, item),
      quantity: item.quantity,
    }
  })
  trackViewCart({
    totalPrice,
    contents,
  })

  if (loading) {
    return (
      <Layout headerVariant="default" footer={false}>
        <Container
          sx={{
            pt: [51, null, 0],
          }}
        >
          <Text>Loading</Text>
        </Container>
      </Layout>
    )
  }

  return (
    <CheckoutFlowLayout
      main={
        <Box
          data-cy="cart-items"
          sx={{
            mt: ['20px', space.l],
            mb: ['30px', null, 0],
          }}
        >
          {items?.map(item => (
            <CartItem
              variant="primary"
              item={item}
              key={`${item.variant.id}${item.quantity}`}
              onQuantityUpdate={updateQuantity}
              source="cart-page"
            />
          ))}
        </Box>
      }
      side={
        <Box>
          <Heading
            variant="title5"
            as="h3"
            sx={{
              mb: ['15px', space.md1],
              pb: ['15px', space.md1],
              borderBottom: '1px solid',
              borderColor: 'border',
              pl: [0, 12, 0],
              pr: [0, 12, 0],
              fontSize: ['22px!important', '33px!important'],
              textTransform: ['uppercase'],
            }}
          >
            Order summary
          </Heading>
          <Box
            sx={{
              pl: [0, 12, 0],
              pr: [0, 12, 0],
            }}
          >
            <table sx={{ width: ['100%'] }}>
              <tbody>
                <tr>
                  <td>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        textTransform: 'uppercase',
                        fontSize: ['12px', '15px;'],
                        color: '#000',
                        fontWeight: 600,
                        mb: ['10px', '15px'],
                        fontFamily:
                          'Roboto, Helvetica, system-ui, -apple-system, BlinkMacSystemFont',
                      }}
                    >
                      Subtotal
                      <div>{checkout ? formatMoney(totalPrice) : formatMoney('0')}</div>
                    </Box>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        textTransform: 'uppercase',
                        fontSize: ['12px', '15px;'],
                        color: '#000',
                        fontWeight: 600,
                        mb: ['10px', '15px'],
                        fontFamily:
                          'Roboto, Helvetica, system-ui, -apple-system, BlinkMacSystemFont',
                      }}
                    >
                      Shipping
                      <div>TBD</div>
                    </Box>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        textTransform: 'uppercase',
                        fontSize: ['12px', '15px;'],
                        color: '#000',
                        fontWeight: 600,
                        mb: ['10px', '15px'],
                        fontFamily:
                          'Roboto, Helvetica, system-ui, -apple-system, BlinkMacSystemFont',
                      }}
                    >
                      Tax
                      <div>TBD</div>
                    </Box>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        textTransform: 'uppercase',
                        fontSize: ['12px', '15px;'],
                        color: '#000',
                        fontWeight: 600,
                        mb: '25px',
                        borderTop: '1px solid',
                        borderColor: '#E6E6E6',
                        width: '100%',
                        fontFamily:
                          'Roboto, Helvetica, system-ui, -apple-system, BlinkMacSystemFont',
                        mt: ['0', '10px'],
                        pt: ['20px', '25px'],
                      }}
                    >
                      Estimated Total{' '}
                      <div>{checkout ? formatMoney(totalPrice) : formatMoney('0')}</div>
                    </Box>
                  </td>
                </tr>
              </tfoot>
            </table>
            {checkout && <CheckoutButton />}
          </Box>
        </Box>
      }
      title="Cart"
      totalItems={items.length}
    />
  )
}

export default CartSummaryPage
